import React, { useState, useRef, useEffect } from 'react';
import { Button, Text } from '@pancakeswap/uikit';
import { styled } from 'styled-components';

const MaxButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const MaxButton = styled(Button)`
  font-size: 14px;
  font-weight: 400;
  background: transparent !important;
  color: ${({ theme }) => theme.colors.primaryBright};
  border-radius: 20px;
  gap: 8px;
  box-shadow: none;
  padding: 4px 8px;
  height: 24px;

  @media (max-width: 480px) {
    font-size: 11px;
    height: 20px;
    padding: 2px 6px;
  }
`;

const DropdownContent = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  background:rgb(42, 23, 80);
  min-width: 50px;
  height: 80px;
  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
  z-index: 1;
  border-radius: 8px;
  right: -5px;
  bottom: 100%; /* Position above the button instead of below */
  margin-bottom: 4px; /* Add margin to the bottom instead of top */
  overflow: hidden;
  padding: 4px; /* Add padding inside the dropdown */
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #3D1D7A;
  border-radius: 6px;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PercentButton = styled(Button)`
  background: transparent;
  border: none;
  padding: 0px 0px;
  font-size: 14px;
  height: 32px;
  text-align: center;
  cursor: pointer;
  border-radius: 0;
  box-shadow: none;
  
  &:hover {
    background-color: #333333;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    padding: 6px 10px;
  }
`;

interface MaxDropdownProps {
  onClick?: () => void;
  onMax: () => void;
  onPercentInput?: (percent: number) => void;
  disabled?: boolean;
}

const MaxDropdown: React.FC<MaxDropdownProps> = ({ onMax, onPercentInput, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Handle clicks outside the dropdown to close it
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing to allow clicking options
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleMaxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMax();
    setIsOpen(false);
  };

  const handlePercentClick = (e: React.MouseEvent, percent: number) => {
    e.stopPropagation();
    if (onPercentInput) {
      onPercentInput(percent);
    }
    setIsOpen(false);
  };

  return (
    <MaxButtonContainer
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MaxButton
        onClick={(e) => {
          e.stopPropagation();
          onMax();
          toggleDropdown(e)
        }}
        disabled={disabled}
      >
        <Text fontSize={["11px", "14px"]} color="inherit">Max</Text>
      </MaxButton>
      <DropdownContent isOpen={isOpen}>
        <ButtonsWrapper>
          <PercentButton onClick={(e) => handlePercentClick(e, 25)}>
            <Text fontSize={["11px", "14px"]} color="inherit">25%</Text>
          </PercentButton>
          <PercentButton onClick={(e) => handlePercentClick(e, 50)}>
            <Text fontSize={["11px", "14px"]} color="inherit">50%</Text>
          </PercentButton>
          <PercentButton onClick={(e) => handlePercentClick(e, 75)}>
            <Text fontSize={["11px", "14px"]} color="inherit">75%</Text>
          </PercentButton>
        </ButtonsWrapper>
      </DropdownContent>
    </MaxButtonContainer>
  );
};

export default MaxDropdown;